import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import Decimal from 'decimal.js';
import { debounce } from 'lodash-es';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Checkbox, ConfirmationModal, Divider, TextField, Typography } from 'seagull';
import { useOverlayContext, useStoreActions } from '~/contexts';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { helpArticles } from '~/utils/helpArticles';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import ExternalLinkButton from '../ExternalLinkButton';
import styles from './RequestPartialRefund.module.scss';
const RequestPartialRefund = (param)=>{
    let { relationship, id: invoiceId, rawTotalAmount, payment: invoicePayment, dismissModal, onSuccess, refreshInvoice } = param;
    const { t: partialRefundTexts } = useLocaleKeys('invoice.partialRefund');
    const { t: commonButtons } = useLocaleKeys('common.buttons');
    const { t: errorTexts } = useLocaleKeys('errors');
    const { api, logBI } = useStoreActions();
    const [payment, setPayment] = useState(invoicePayment);
    const isCustomerFees = payment.isCustomerFees();
    const creditCardFees = payment.creditCardFees();
    const remainingAmount = payment.remainingAmount() || rawTotalAmount;
    useEffect(()=>{
        setPayment(invoicePayment);
    }, [
        invoicePayment
    ]);
    const { handleSubmit, control, setValue, watch, setError, formState: { errors } } = useForm({
        mode: 'all',
        defaultValues: {
            refundAmount: remainingAmount,
            reason: '',
            refundFees: isCustomerFees
        }
    });
    const { startLoader, stopLoader, showErrorToast, showSuccessToast } = useOverlayContext();
    const { loading, refresh } = useRestfulWrapper({
        initialArgs: {
            refundAmount: remainingAmount.toString(),
            refundFees: isCustomerFees
        },
        fetch: async (param)=>{
            let { refundAmount, refundFees } = param;
            if (errors.refundAmount) return {
                refundAmount
            };
            const { success, data, error } = await handleRequest(api.invoice.calculateRefund(invoiceId, {
                amount: refundAmount,
                refundFees
            }));
            if (error || !success) return {
                refundAmount
            };
            return {
                refundAmount: data.refundAmount
            };
        }
    });
    const [totalRefundAmount, refundFees] = watch([
        'refundAmount',
        'refundFees'
    ]);
    const businessDaysRange = payment.isFeesAch() && payment.isPaymentProcessorColumn() ? '1-2' : '5-10';
    useEffect(()=>{
        const debouncedRefresh = debounce((refundAmount, refundFees)=>{
            refresh({
                args: {
                    refundAmount,
                    refundFees
                }
            });
        }, 300);
        debouncedRefresh(totalRefundAmount.toString(), refundFees);
        return ()=>debouncedRefresh.cancel();
    }, [
        totalRefundAmount,
        refundFees
    ]);
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'request_partial_refund_confirm'
            },
            secondaryButton: {
                name: 'request_partial_refund_cancel'
            }
        },
        testId: "request-refund-modal",
        secondaryButtonLabel: commonButtons.cancel(),
        onPrimaryButtonClick: handleSubmit(async (param)=>{
            let { reason, refundAmount, refundFees } = param;
            startLoader();
            const { success, error } = await handleRequest(api.invoice.refundPartialPayment(invoiceId, {
                refundAmount: refundAmount.toString(),
                reason,
                refundFees
            }));
            if (!success) {
                const { invoice } = await refreshInvoice();
                if (!invoice.payment) {
                    showErrorToast(errorTexts.unexpected(), 'request-refund-error-toast');
                    stopLoader();
                    return;
                }
                setPayment(invoice.payment);
                const remainingAmount = payment.remainingAmount() || rawTotalAmount;
                if (!remainingAmount) {
                    showErrorToast(errorTexts.unexpected(), 'request-refund-error-toast');
                    stopLoader();
                    return;
                }
                if (error === null || error === void 0 ? void 0 : error.hasServerError(ServerError.InvoiceRefundAmountExceeds)) {
                    setError('refundAmount', {
                        message: partialRefundTexts.amountCannotBeMoreThan({
                            remainingAmount: remainingAmount.toString()
                        })
                    });
                    showErrorToast(partialRefundTexts.refundExceedsAmount(), 'request-refund-error-toast');
                } else if (error === null || error === void 0 ? void 0 : error.hasServerError(ServerError.InvoiceAlreadyHasOneRefundNotRefunded)) {
                    showErrorToast(partialRefundTexts.previousRefundProcessing(), 'request-refund-error-toast');
                } else if (error === null || error === void 0 ? void 0 : error.hasServerError(ServerError.InvoiceIsFullyRefunded)) {
                    dismissModal();
                    showErrorToast(partialRefundTexts.invoiceFullyRefunded(), 'request-refund-error-toast');
                } else {
                    showErrorToast(errorTexts.unexpected(), 'request-refund-error-toast');
                }
            } else {
                onSuccess({
                    reason,
                    refundAmount: refundAmount.toString(),
                    refundFees
                });
                dismissModal();
                showSuccessToast(partialRefundTexts.refundRequestSubmitted(), 'request-refund-success-toast');
                const isFullRefund = new Decimal(refundAmount).eq(new Decimal(rawTotalAmount));
                const relationshipId = relationship.id;
                logBI(isFullRefund ? 19000 : 19001, {
                    relationshipId
                });
            }
            stopLoader();
        }),
        onSecondaryButtonClick: dismissModal,
        primaryButtonTestId: "request-refund-submit",
        secondaryButtonTestId: "request-refund-cancel",
        title: partialRefundTexts.title(),
        primaryButtonLabel: partialRefundTexts.refundButton(),
        width: "720",
        primaryButtonLoading: loading,
        content: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.fieldsWrapper,
                    children: [
                        /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "refundAmount",
                            rules: {
                                validate: (value)=>{
                                    if (!value) return partialRefundTexts.pleaseEnterValidAmount();
                                    const amount = new Decimal(value);
                                    if (amount.gt(remainingAmount)) return partialRefundTexts.amountCannotBeMoreThan({
                                        remainingAmount: remainingAmount.toString()
                                    });
                                    if (amount.lte(0)) return partialRefundTexts.amountMustBePositive();
                                    return true;
                                }
                            },
                            render: (props)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                                    bi: {
                                        name: 'refund_amount'
                                    },
                                    collapseHelperText: false,
                                    width: "45%",
                                    autoFocus: true,
                                    isMoney: true,
                                    prefix: "$",
                                    label: partialRefundTexts.amountLabel(),
                                    testId: "request-refund-refund-amount"
                                }, connectControllerToTextField(props)))
                        }),
                        payment.isCustomerFees() && /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "refundFees",
                            render: (props)=>/*#__PURE__*/ _jsx(Checkbox, {
                                    onChange: (e)=>{
                                        setValue('refundFees', e.target.checked);
                                    },
                                    checked: props.field.value,
                                    className: styles.checkbox,
                                    testId: "refund-fees-checkbox",
                                    bi: {
                                        name: 'refund_fees'
                                    },
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                align: "left",
                                                children: partialRefundTexts.reimburseClientLabel()
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                skin: "secondary",
                                                align: "left",
                                                testId: "refund-fees-info",
                                                children: partialRefundTexts.reimburseClientDescription({
                                                    feesAmount: String(creditCardFees === null || creditCardFees === void 0 ? void 0 : creditCardFees.feesAmount)
                                                })
                                            })
                                        ]
                                    })
                                })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Divider, {}),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.reasonContainer,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            weight: "bold",
                            variant: "h6",
                            children: partialRefundTexts.reasonTitle()
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: partialRefundTexts.reasonDescription()
                        }),
                        /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "reason",
                            rules: requiredValidation(partialRefundTexts.reasonRequired()),
                            render: (props)=>/*#__PURE__*/ _jsx("div", {
                                    className: styles.inputField,
                                    children: /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'refund_reason'
                                        },
                                        collapseHelperText: false,
                                        multiline: true,
                                        minRows: 2,
                                        maxRows: 3
                                    }, connectControllerToTextField(props)), {
                                        testId: "request-refund-reason-field"
                                    }))
                                })
                        }),
                        /*#__PURE__*/ _jsxs(Typography, {
                            className: styles.infoParagraph,
                            testId: "request-refund-business-days-range",
                            children: [
                                partialRefundTexts.refundInfoDescription({
                                    businessDaysRange
                                }),
                                ' ',
                                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                    openInNewTab: true,
                                    href: helpArticles.refunds(),
                                    underline: "none",
                                    bi: {
                                        name: 'refunds_learn_more'
                                    },
                                    children: partialRefundTexts.learnMore()
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
};
export default RequestPartialRefund;
